// @djsem/render-state — scene + venue contracts (renderer-agnostic, simulation/adapter-ready).
//
// Rule 7 is ENFORCED, not just documented: visual-scene validation rejects any string
// containing renderer/hardware vocabulary (TouchDesigner nodes, shader names, pixel
// coordinates, DMX channels...). Rule 9 is enforced at the state level: blackout /
// emergency states must carry zero intensity — a state object claiming blackout with
// light on is INVALID by contract.
import {
  vArray, vBoolean, vForbiddenTerms, vId, vNumber, vObject, vRefine, vString, vUnit,
  type Validator, type ValidationResult,
} from "@djsem/shared";

// ---- forbidden vocabulary guards -------------------------------------------------

/** Pattern rejecting renderer/hardware vocabulary (engine nodes, shaders, pixels, protocols) in visual scenes. */
export const VISUAL_FORBIDDEN = /touch\s?designer|\btox\b|shader|glsl|hlsl|\bpixel(s)?\b|\bdmx\b|art-?net|\bsacn\b|pangolin|\bfb4\b|\bilda\b|resolume|madmapper/i;
/** Pattern rejecting real hardware protocol/address vocabulary in lighting scenes. */
export const LIGHTING_FORBIDDEN = /\bdmx\b|art-?net|\bsacn\b|pangolin|\bfb4\b|\bilda\b|universe\s*\d|address\s*\d/i;

// ---- visual scenes ----------------------------------------------------------------

/** The narrative/emotional intent a visual scene expresses. */
export type VisualIntent = "ambient" | "build" | "burst" | "impact" | "release" | "recovery" | "closing" | "blackout" | "safe";
/** Alias of {@link VisualIntent} used by the newer render-state contracts. */
export type DJSEMVisualSceneIntent = VisualIntent;
/** Simulated motion character of a visual/lighting scene. */
export type VisualMotion = "none" | "slow" | "medium" | "fast" | "rhythmic" | "rising" | "impact";
/** Safety tier of a scene; `restricted` requires explicit operator approval. */
export type SceneSafetyLevel = "safe" | "standard" | "high_intensity" | "restricted";
/** A renderer-neutral visual scene: pure meaning (intent/intensity/motion/symbols), never a renderer command. */
export interface VisualScene {
  id: string;
  label: string;
  description?: string;
  semanticUses: string[];
  chapterUses: string[];
  brandPaletteId?: string;
  visualIntent: VisualIntent;
  intensity: number;
  motion: VisualMotion;
  safetyLevel: SceneSafetyLevel;
  rendererHintsAllowed: string[]; // abstract hint NAMES a renderer may honor — never commands
  // Prompt 10B additions (optional so existing scenes stay valid; engine defaults them):
  cueUses?: string[];
  density?: number;
  brandWorldId?: string;
  symbolPolicy?: { allowedSymbols: string[]; blockedSymbols: string[] };
}
/** Alias of {@link VisualScene} used by the newer render-state contracts. */
export type DJSEMVisualScene = VisualScene;
const vSafetyLevel = vString({ enum: ["safe", "standard", "high_intensity", "restricted"] });
const vVisualSceneShape: Validator<VisualScene> = vObject(
  {
    id: vId(), label: vString({ min: 1 }),
    semanticUses: vArray(vString({ min: 1 })), chapterUses: vArray(vString({ min: 1 })),
    visualIntent: vString({ enum: ["ambient", "build", "burst", "impact", "release", "recovery", "closing", "blackout", "safe"] }),
    intensity: vUnit(),
    motion: vString({ enum: ["none", "slow", "medium", "fast", "rhythmic", "rising", "impact"] }),
    safetyLevel: vSafetyLevel,
    rendererHintsAllowed: vArray(vString({ min: 1 })),
  },
  {
    description: vString({}), brandPaletteId: vId(), brandWorldId: vId(),
    cueUses: vArray(vString({ min: 1 })), density: vUnit(),
    symbolPolicy: vObject({ allowedSymbols: vArray(vString({})), blockedSymbols: vArray(vString({})) }),
  },
) as Validator<VisualScene>;
/** Validator for {@link VisualScene}; also enforces the renderer-neutral vocabulary guard. */
export const vVisualScene: Validator<VisualScene> = (v, path = "$") => {
  const shape = vVisualSceneShape(v, path);
  if (!shape.valid) return shape;
  const guard = vForbiddenTerms(VISUAL_FORBIDDEN, "visual scene must stay renderer-neutral")(v, path);
  return guard as ValidationResult;
};

// ---- lighting scenes ---------------------------------------------------------------

/** A fade/snap/build/release transition into or out of a lighting scene. */
export interface LightingTransition { kind: "fade" | "snap" | "build" | "release"; durationMs: number }
/** Resolved simulated state of one fixture group within a (simpler, show-direction) {@link LightingScene}. */
export interface FixtureGroupState {
  fixtureGroupId: string;
  intensity: number;
  motion: "static" | "sweep" | "chase" | "breathe" | "pulse" | "strobe_safe";
  paletteRole?: string; // brand palette ROLE name (e.g. "peak_alt"), never a raw color channel
}
/**
 * A simpler, show-direction lighting scene contract (distinct from the richer venue-rig
 * lighting model in lightingSceneEngine.ts, which is intentionally left separate).
 */
export interface LightingScene {
  id: string;
  label: string;
  description: string;
  chapterUses: string[];
  semanticUses: string[];
  brandPaletteId?: string; // optional brand attachment (presentation only; never touches safety/semantics)
  stemDrivers: string[]; // stem types allowed to modulate this scene (metadata-driven only)
  fixtureGroupStates: FixtureGroupState[];
  transitionIn?: LightingTransition;
  transitionOut?: LightingTransition;
  safetyLevel: SceneSafetyLevel;
}
const vLightingTransition: Validator<LightingTransition> = vObject(
  { kind: vString({ enum: ["fade", "snap", "build", "release"] }), durationMs: vNumber({ min: 0 }) },
) as Validator<LightingTransition>;
/** Validator for {@link FixtureGroupState}. */
export const vFixtureGroupState: Validator<FixtureGroupState> = vObject(
  {
    fixtureGroupId: vId(), intensity: vUnit(),
    motion: vString({ enum: ["static", "sweep", "chase", "breathe", "pulse", "strobe_safe"] }),
  },
  { paletteRole: vString({ min: 1 }) },
) as Validator<FixtureGroupState>;
const vLightingSceneShape: Validator<LightingScene> = vObject(
  {
    id: vId(), label: vString({ min: 1 }), description: vString({}),
    chapterUses: vArray(vString({ min: 1 })), semanticUses: vArray(vString({ min: 1 })),
    stemDrivers: vArray(vString({ enum: ["drums", "bass", "vocals", "melody", "other"] })),
    fixtureGroupStates: vArray(vFixtureGroupState, { min: 1 }),
    safetyLevel: vSafetyLevel,
  },
  { transitionIn: vLightingTransition, transitionOut: vLightingTransition },
) as Validator<LightingScene>;
/** Validator for {@link LightingScene}; also enforces the protocol-free vocabulary guard. */
export const vLightingScene: Validator<LightingScene> = (v, path = "$") => {
  const shape = vLightingSceneShape(v, path);
  if (!shape.valid) return shape;
  return vForbiddenTerms(LIGHTING_FORBIDDEN, "lighting scene must stay protocol-free")(v, path) as ValidationResult;
};

// ---- venue production rig ------------------------------------------------------------

/** A named area of a venue in this (scenes.ts) rig contract. */
export interface VenueZone { id: string; label: string }
/** A named group of simulated fixtures in this (scenes.ts) rig contract. */
export interface FixtureGroup { id: string; label: string; zoneId: string; role: "wash" | "beam" | "strobe" | "blinder" | "pixel_group" | "moving" | "atmosphere" | "house" }
/** A simulated display screen (projection/LED) within a venue zone. */
export interface VenueScreen { id: string; label: string; zoneId: string; aspect: string }
/** Safety ceilings/permissions a venue rig grants to automation. */
export interface VenueSafetyProfile {
  maxGlobalIntensity: number;      // 0..1 ceiling automation may request
  strobeAllowed: boolean;
  laserSimulationAllowed: boolean; // SIMULATION only — real laser output does not exist here
  audienceScanSimulationAllowed: boolean;
}
/** Whether a rig contract represents pure simulation, a saved venue profile, or an external adapter profile. */
export type VenueRigType = "simulation" | "venue_profile" | "external_adapter_profile";
/** A venue production rig contract: zones, fixture groups, screens, and safety profile. */
export interface VenueProductionRigContract {
  id: string;
  name: string;
  type: VenueRigType;
  zones: VenueZone[];
  fixtureGroups: FixtureGroup[];
  screens: VenueScreen[];
  safetyProfile: VenueSafetyProfile;
}
/** Validator for {@link VenueProductionRigContract}. */
export const vVenueProductionRig: Validator<VenueProductionRigContract> = vObject(
  {
    id: vId(), name: vString({ min: 1 }),
    type: vString({ enum: ["simulation", "venue_profile", "external_adapter_profile"] }),
    zones: vArray(vObject({ id: vId(), label: vString({ min: 1 }) }), { min: 1 }),
    fixtureGroups: vArray(vObject({
      id: vId(), label: vString({ min: 1 }), zoneId: vId(),
      role: vString({ enum: ["wash", "beam", "strobe", "blinder", "pixel_group", "moving", "atmosphere", "house"] }),
    }), { min: 1 }),
    screens: vArray(vObject({ id: vId(), label: vString({ min: 1 }), zoneId: vId(), aspect: vString({ pattern: /^\d+:\d+$/ }) })),
    safetyProfile: vObject({
      maxGlobalIntensity: vUnit(), strobeAllowed: vBoolean,
      laserSimulationAllowed: vBoolean, audienceScanSimulationAllowed: vBoolean,
    }),
  },
) as Validator<VenueProductionRigContract>;

// ---- venue production state -----------------------------------------------------------

/** Current safety mode of a venue production state. Blackout/emergency force zero intensity everywhere. */
export type VenueSafetyMode = "normal" | "safe_idle" | "blackout" | "emergency";
/** A dry-run-ready event describing a venue production state change, for downstream adapters. */
export interface DJSEMAdapterReadyEvent {
  id: string;
  timeMs: number;
  kind: string;              // internal event kind (e.g. "lighting_scene_entered")
  targetType: "lighting" | "visual" | "laser_sim" | "control";
  payload: Record<string, unknown>;
}
/** Resolved venue production state: active scenes/fixtures, intensity, and safety mode. */
export interface VenueProductionStateContract {
  timeMs: number;
  activeLightingScenes: string[];
  activeLaserConfig?: string;   // simulation config id only
  activeFixtureGroups: FixtureGroupState[];
  globalIntensity: number;
  safetyMode: VenueSafetyMode;
  triggerSource: string;
  adapterReadyEvents: DJSEMAdapterReadyEvent[];
}
const vAdapterReadyEvent: Validator<DJSEMAdapterReadyEvent> = vObject(
  {
    id: vId(), timeMs: vNumber({ min: 0 }), kind: vString({ min: 1 }),
    targetType: vString({ enum: ["lighting", "visual", "laser_sim", "control"] }),
    payload: (v, path = "$") => (typeof v === "object" && v !== null && !Array.isArray(v) ? { valid: true, issues: [] } : { valid: false, issues: [{ path, message: "expected object payload" }] }),
  },
) as Validator<DJSEMAdapterReadyEvent>;
/**
 * Validator for {@link VenueProductionStateContract}. Enforces the safety invariant at the
 * contract level: `blackout`/`emergency` safety mode requires zero global intensity and
 * every fixture group at zero intensity.
 */
export const vVenueProductionState: Validator<VenueProductionStateContract> = vRefine(
  vObject(
    {
      timeMs: vNumber({ min: 0 }),
      activeLightingScenes: vArray(vId()),
      activeFixtureGroups: vArray(vFixtureGroupState),
      globalIntensity: vUnit(),
      safetyMode: vString({ enum: ["normal", "safe_idle", "blackout", "emergency"] }),
      triggerSource: vString({ min: 1 }),
      adapterReadyEvents: vArray(vAdapterReadyEvent),
    },
    { activeLaserConfig: vId() },
  ) as Validator<VenueProductionStateContract>,
  // RULE 9 AT THE CONTRACT LEVEL: blackout/emergency ⇒ nothing may be lit.
  (s) => {
    if ((s.safetyMode === "blackout" || s.safetyMode === "emergency")) {
      if (s.globalIntensity !== 0) return `safetyMode ${s.safetyMode} requires globalIntensity 0`;
      if (s.activeFixtureGroups.some((g) => g.intensity > 0)) return `safetyMode ${s.safetyMode} requires all fixture groups at 0`;
    }
    return null;
  },
);
